/**
 * Retry utility for upload operations.
 * Wraps async functions with exponential backoff retry logic.
 */

export interface RetryStatus {
  attempt: number;
  maxRetries: number;
  error: Error;
}

/**
 * Retry an async function with exponential backoff.
 *
 * @param fn - The async function to retry
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @param onRetry - Optional callback fired before each retry with status info
 * @returns The result of the successful call
 * @throws The last error if all retries are exhausted
 */
export async function retryUpload<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  onRetry?: (status: RetryStatus) => void
): Promise<T> {
  let lastError: Error = new Error('Upload failed');

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));

      if (attempt < maxRetries) {
        if (onRetry) {
          onRetry({ attempt, maxRetries, error: lastError });
        }
        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, attempt - 1) * 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}
